import { Outlet, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../store/user/useUserStore';
import { selectUser } from '../../store/user/userSelectors';
import HelpIcon from '@mui/icons-material/Help';
import MenuIcon from '@mui/icons-material/Menu';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TabIcon from '@mui/icons-material/Tab';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { NavBarLayout } from '@tassomutuo-backoffice/ui';
import configEnv from '../../config/config';
import logo from '../../assets/images/logo_tassomutuo.png';
import { getApiTokensInLocalStorage } from '../../services/localStorage/localStorage';
import { logout } from '../../services/api/auth/auth';
import { clearAuthenticationInLocalStorage } from '../../services/localStorage/localStorage';
import { clearTassoQuintoSession } from '../../services/api/tasso-quinto';
import ImpersonatesConsultant from '../../components/ImpersonatesConsultant/ImpersonatesConsultant';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ForumIcon from '@mui/icons-material/Forum';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { removeDashboardFiltersInSessionStorage } from '../../services/sessionStorage/sessionStorage';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { MANAGED_LEAD_PRODUCTS } from '../../config/managedLeadNavigation';
const ProtectedPageLayout = () => {
  const navigate = useNavigate();
  const user = useUserStore(selectUser);
  const role = user?.role;
  const userIsTeamManager = !!user?.team;

  async function onLogout() {
    const refreshToken = getApiTokensInLocalStorage()?.refresh?.token;
    clearAuthenticationInLocalStorage();
    clearTassoQuintoSession();
    removeDashboardFiltersInSessionStorage();
    navigate('/login');
    if (refreshToken) {
      await logout(refreshToken);
    }
  }

  const adminOptionsToShow = [
    {
      id: 'admin',
      name: 'Admin',
      path: null,
      position: 'top',
      renderIcon: () => <AdminPanelSettingsIcon sx={{ color: 'inherit' }} />,
      subItems: [
        {
          id: 'admin-dashboard',
          name: 'Statistiche',
          path: '/admin/dashboard',
          renderIcon: () => <QueryStatsIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-menu',
          name: 'Menu',
          path: '/admin/navigation-menu',
          renderIcon: () => <MenuIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-footer',
          name: 'Menu footer',
          path: '/admin/navigation-footer',
          renderIcon: () => <InsertPageBreakIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-bank-infos',
          name: 'Informazioni Bancarie',
          path: '/admin/bank-infos',
          renderIcon: () => <AccountBalanceIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-pages',
          name: 'Pagine',
          path: '/admin/pages',
          renderIcon: () => <TabIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-profiles',
          name: 'Profili',
          path: '/admin/profiles',
          renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-quotes',
          name: 'Preventivi',
          path: '/admin/quotes',
          renderIcon: () => <RequestQuoteIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-drop',
          name: 'Drop requests',
          path: '/admin/drop',
          renderIcon: () => <DeleteSweepIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-retraining',
          name: 'Cappello parlante',
          path: '/admin/cappelloparlante',
          renderIcon: () => <AutoFixHighIcon sx={{ color: 'inherit' }} />,
        },
      ],
    },
    {
      id: 'admin-teams-groups',
      name: 'Gestione team',
      path: null,
      renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
      subItems: [
        {
          id: 'admin-teams',
          name: 'Teams',
          path: '/admin/teams/management',
          renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-teams-managers',
          name: 'Teams Managers',
          path: '/admin/teams/managers',
          renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
        },
        {
          id: 'admin-teams-groups',
          name: 'Dettaglio team',
          path: '/admin/teams',
          renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
        },
      ],
    },
  ];

  const adminOnlyLeadOptions =
    role === 'admin'
      ? [
          {
            id: 'external-leads',
            name: 'Lead esterne',
            path: '/admin/external-leads',
            renderIcon: () => <PersonSearchIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
          {
            id: 'consultation-requests',
            name: 'Richieste di consulenza',
            path: '/admin/consultation-requests',
            renderIcon: () => <ForumIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
          {
            id: 'lead-tasso-new',
            name: 'Acquisizione Lead Tasso',
            path: '/admin/lead-tasso/new',
            renderIcon: () => <PostAddIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
        ]
      : [];

  const managedLeadOptions = MANAGED_LEAD_PRODUCTS.map((product) => {
    const ProductIcon = product.icon;

    return {
      id: product.id,
      name: product.name,
      path: null,
      activePaths: product.activePaths,
      position: 'top',
      renderIcon: () => <ProductIcon sx={{ color: 'inherit' }} />,
      subItems: product.categories.map((category) => {
        const CategoryIcon = category.icon;

        return {
          id: `${product.id}-${category.id}`,
          name: category.name,
          path: category.path,
          renderIcon: () => <CategoryIcon sx={{ color: 'inherit' }} />,
        };
      }),
    };
  });

  const allRoleOptionsToShow = [
    ...managedLeadOptions,
    ...adminOnlyLeadOptions,
    {
      id: 'tasks',
      name: 'Promemoria',
      path: '/tasks',
      renderIcon: () => <TaskAltIcon sx={{ color: 'inherit' }} />,
      position: 'top',
    },
    {
      id: 'help',
      name: 'Help',
      path: '/help',
      renderIcon: () => <HelpIcon sx={{ color: 'inherit' }} />,
      position: 'top',
    },
    {
      id: 'profile',
      name: 'Profilo',
      path: '/profile',
      position: 'bottom',
      renderIcon: () => <AccountCircleIcon sx={{ color: 'inherit' }} />,
    },
  ];

  const teamManagerOptionsToShow = [
    {
      id: 'admin-teams-groups',
      name: 'Dettaglio team',
      path: `/admin/teams/${user?.team?.id}`,
      renderIcon: () => <PeopleAltIcon sx={{ color: 'inherit' }} />,
    },
  ];

  const dashboardOption = [
    ...(role === 'admin' || userIsTeamManager
      ? [
          {
            id: 'dashboard',
            name: 'Dashboard',
            path: '/dashboard',
            renderIcon: () => <DashboardIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
          {
            id: 'deep-dive',
            name: 'Deep dive consulenti',
            path: '/dashboard/deep-dive',
            renderIcon: () => <PersonSearchIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
          {
            id: 'fatturato-perso',
            name: 'Fatturato perso',
            path: '/dashboard/fatturato-perso',
            renderIcon: () => <TrendingDownIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
        ]
      : [
          {
            id: 'dashboard',
            name: 'Dashboard',
            path: '/dashboard',
            renderIcon: () => <DashboardIcon sx={{ color: 'inherit' }} />,
            position: 'top',
          },
        ]),
  ];

  const menuOptionsToShow = [
    ...dashboardOption,
    ...allRoleOptionsToShow,
    ...(role === 'admin' ? adminOptionsToShow : []),
    ...(role !== 'admin' && userIsTeamManager ? teamManagerOptionsToShow : []),
  ];

  return (
    <NavBarLayout
      menuOptions={menuOptionsToShow}
      logo={logo}
      onLogoutPress={onLogout}
      appVersion={configEnv.appVersion}
      renderCustomContent={<ImpersonatesConsultant />}
    >
      <Outlet />
    </NavBarLayout>
  );
};

export default ProtectedPageLayout;
